// Libraries
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useLocation, Link } from "react-router-dom";

// Components
import AdHelper from "../shared/ad/AdHelper";
import Careers from "../shared/careers/CareerTab";
import CategoryTags from "../news/CategoryTags";
import Loading from "../Loading";
import NewsCard from "../news/NewsCard";
import AdInRow from "../ad/AdInRow";
import FilterComponent from "../news/FilterComponent";

// Ant Design
import { Empty, message, BackTop, Button, Row, Col } from "antd";
import { PlusOutlined } from "@ant-design/icons";

// Helpers & Config
import NoCapManifest from "../../nocap.json";
import config from "../../config";
import axios from "axios";
import { capitalizeFirstLetterOfEachWord } from "../../helpers";

// safer JSON parse
const safeParse = (v, fallback = {}) => {
  try {
    if (!v || v === "undefined" || v === "null") return fallback;
    return JSON.parse(v);
  } catch {
    return fallback;
  }
};

const NewsPage = () => {
  const [currentCategory, setCurrentCategory] = useState("");
  const [districts, setDistricts] = useState(
    NoCapManifest.candidateInfoForm.districts
  );
  const [inputState, setInputState] = useState({
    state: "",
    division: "",
    district: "",
  });

  const [data, setData] = useState([]);
  const [isFilteringActive, setIsFilteringActive] = useState(false);

  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const location = useLocation();
  const didRestoreOnce = useRef(false);

  // ✅ safe user parse (prevents random crash)
  const user = safeParse(localStorage.getItem("user"), {});
  const isAdmin = user.role === "admin";

  // Fetch ONCE
  const fetchDataOnce = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios(`${config.BLOGS}/blogs/all`);
      setData(Array.isArray(response.data) ? response.data : []);
    } catch (e) {
      setError(e);
      console.error("Error fetching data:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDataOnce();
  }, [fetchDataOnce]);

  // Restore scroll/filter/category ONCE (no refetch)
  useEffect(() => {
    if (didRestoreOnce.current) return;
    didRestoreOnce.current = true;

    const restoredCategory = location.state?.category;
    const restoredFilters = location.state?.filters;
    const restoredScroll = location.state?.scrollY;

    if (restoredCategory) setCurrentCategory(restoredCategory);

    const filtersAreValid =
      restoredFilters &&
      Object.values(restoredFilters).some((v) => v && v.trim() !== "");

    if (filtersAreValid) {
      setInputState(restoredFilters);
      setIsFilteringActive(true);
    }

    if (restoredScroll !== undefined) {
      setTimeout(() => window.scrollTo(0, restoredScroll), 200);
    }
  }, [location.state]);

  // ✅ Local update hook: called after vote/comment
  const onPostUpdated = useCallback((updatedPost) => {
    if (!updatedPost?._id) return;
    setData((prev) =>
      (prev || []).map((p) => (p._id === updatedPost._id ? updatedPost : p))
    );
  }, []);

  // Apply filtering locally
  const filteredData = useMemo(() => {
    const state = (inputState.state || "").trim();
    const district = (inputState.district || "").trim();

    const shouldFilterState = isFilteringActive && state !== "";
    const shouldFilterDistrict = isFilteringActive && district !== "";

    return (data || []).filter((item) => {
      if (item?.archived) return false;

      const categoryMatch = currentCategory
        ? item?.category === currentCategory
        : true;
      if (!categoryMatch) return false;

      const stateMatch = shouldFilterState ? item?.state?.includes(state) : true;
      if (!stateMatch) return false;

      const districtMatch = shouldFilterDistrict
        ? item?.district?.includes(district)
        : true;
      if (!districtMatch) return false;

      return true;
    });
  }, [
    data,
    currentCategory,
    inputState.state,
    inputState.district,
    isFilteringActive,
  ]);

  // Handlers
  const handleCategoryChange = (c) => setCurrentCategory(c);

  const handleFilter = (filters) => {
    setInputState(filters);
    setIsFilteringActive(true);
  };

  const handleFilterClear = () => {
    setInputState({ state: "", division: "", district: "" });
    setIsFilteringActive(false);
  };

  const addDistrict = (e) => {
    e.preventDefault();
    const i = Date.now();
    const addedDistrict = inputState.district
      ? capitalizeFirstLetterOfEachWord(inputState.district)
      : `New Area ${i}`;

    if (districts.includes(addedDistrict)) {
      message.error("District already exists");
      setInputState({ ...inputState, district: "" });
    } else {
      setDistricts([...districts, addedDistrict]);
      setInputState({ ...inputState, district: "" });
    }
  };

  if (error) {
    message.error("Oops ... Something went wrong loading data");
    return null;
  }

  const blogItems = filteredData.map((post, idx) => (
    <AdInRow count={idx + 1} key={post._id}>
      <Col
        xs={24}
        sm={12}
        md={8}
        lg={6}
        xl={6}
        style={{ padding: "0 1em", marginBottom: "2em" }}
      >
        <NewsCard
          post={post}
          pathname={`/news/${post._id}`}
          currentCategory={currentCategory}
          inputState={inputState}
          onPostUpdated={onPostUpdated} // ✅ important
        />
      </Col>
    </AdInRow>
  ));

  return (
    <div>
      <div className="news-ads-container">
        <AdHelper page="news" />
      </div>

      <br />

      <div className="news-cta-container">
        <CategoryTags
          categories={NoCapManifest.news.newsCategories}
          value={currentCategory}
          onChange={handleCategoryChange}
        />

        {isAdmin && (
          <div style={{ display: "flex", justifyContent: "center", marginTop: 16 }}>
            <Link to="/news/new">
              <Button
                type="primary"
                icon={<PlusOutlined />}
                size="large"
                className="btn-solid"
              >
                Create Article
              </Button>
            </Link>
          </div>
        )}
      </div>

      <br />

      <FilterComponent onFilter={handleFilter} onClear={handleFilterClear} />

      <br />

      {loading ? (
        <Loading />
      ) : filteredData.length > 0 ? (
        <Row className="news-row">{blogItems}</Row>
      ) : currentCategory === "Careers" ? (
        <Careers careerPosts={[]} />
      ) : (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
      )}

      <BackTop />

      <br />

      <div className="news-ads-container">
        <AdHelper page="news" />
      </div>
    </div>
  );
};

export default NewsPage;
